import React from 'react';
import PropTypes from 'prop-types';

import {
    Flex,
    Image,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';
import { TransitionWrapper, Center } from '@zillow/zrs-comet';

import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';

import HeartHome from 'app/shared/images/HeartHome.svg';

import {
    StyledBodyContainer,
    StyledContentContainer,
    StyledDescriptionContainer,
    StyledDesktopTooltip,
    StyledIcon,
    StyledMobileTooltip,
    StyledSideContainer,
    StyledCenterParagraph,
    StyledDesktopTitleParagraph,
    StyledMobileTitleParagraph,
    StyledTooltipIconWrapper,
} from './StyledComponents';


const propTypes = {
    children: PropTypes.node.isRequired,
    description: PropTypes.node,
    icon: PropTypes.node,
    isTransitioning: PropTypes.bool,
    hasError: PropTypes.bool,
    onTooltipHelpClick: PropTypes.func,
    subtitle: PropTypes.node,
    title: PropTypes.string.isRequired,
    mobileTitle: PropTypes.node,
    tooltipHelpText: PropTypes.string,
    tooltipText: PropTypes.node,
};

const defaultProps = {
    description: '',
    icon: null,
    isTransitioning: false,
    hasError: false,
    onTooltipHelpClick: () => {},
    subtitle: '',
    mobileTitle: '',
    tooltipHelpText: '',
    tooltipText: '',
};

const WizardBodyTemplate = ({
    children,
    description,
    icon,
    isTransitioning,
    hasError,
    onTooltipHelpClick,
    subtitle,
    title,
    mobileTitle,
    tooltipHelpText,
    tooltipText,
}) => {
    const renderTooltipContent = () => (
        <Flex display="flex" alignItems="center">
            <StyledTooltipIconWrapper>
                <Image src={HeartHome} alt="" />
            </StyledTooltipIconWrapper>
            <Text fontType="bodySmall">
                {tooltipText}
                &nbsp;
                {tooltipHelpText && (
                    <TextButton fontType="bodySmallHeading" onClick={onTooltipHelpClick}>
                        {tooltipHelpText}
                    </TextButton>
                )}
            </Text>
        </Flex>
    );

    const renderError = () => (
        <Center fontColor="red600">
            {ErrorMessageConstants.DEFAULT_ERROR}
        </Center>
    );

    const renderContent = () => (
        <>
            <Spacer mediaQuery={{ lg: { marginX: 'lg' } }}>
                {icon && (
                    <Flex display="flex" flexDirection="column" alignItems="center" paddingBottom="lg">
                        <StyledIcon>
                            {icon}
                        </StyledIcon>
                    </Flex>
                )}
                <Spacer paddingBottom="xs">
                    <StyledDesktopTitleParagraph fontType="h3">
                        {title}
                    </StyledDesktopTitleParagraph>
                    {mobileTitle ? (
                        <StyledMobileTitleParagraph
                            fontType="h4"
                        >
                            {mobileTitle}
                        </StyledMobileTitleParagraph>
                    ) :
                        <StyledMobileTitleParagraph fontType="h4">{title}</StyledMobileTitleParagraph>}
                    {subtitle && (
                        <Spacer paddingTop="xs">
                            <StyledCenterParagraph fontType="body" data-test="subtitle" className="subtitle">
                                {subtitle}
                            </StyledCenterParagraph>
                        </Spacer>
                    )}
                    {description && (
                        <StyledDescriptionContainer>
                            <StyledCenterParagraph fontType="body" data-test="description">
                                {description}
                            </StyledCenterParagraph>
                        </StyledDescriptionContainer>
                    )}
                </Spacer>
            </Spacer>
            {children}
        </>
    );

    return (isTransitioning ? (
        <StyledBodyContainer>
            <StyledSideContainer />
            <StyledContentContainer borderRounded={true} backgroundColor="white">
                <TransitionWrapper isTransitioning={true} />
            </StyledContentContainer>
            <StyledSideContainer />
        </StyledBodyContainer>
    ) : (
        <>
            {tooltipText && (
                <StyledMobileTooltip>
                    {renderTooltipContent()}
                </StyledMobileTooltip>
            )}
            <StyledBodyContainer>
                <StyledSideContainer />
                <StyledContentContainer
                    borderRounded={true}
                    backgroundColor="white"
                    display="flex"
                    flexDirection="column"
                >
                    {hasError ? renderError() : renderContent()}
                </StyledContentContainer>
                <StyledSideContainer>
                    {tooltipText && (
                        <StyledDesktopTooltip>
                            {renderTooltipContent()}
                        </StyledDesktopTooltip>
                    )}
                </StyledSideContainer>
            </StyledBodyContainer>
        </>
    ));
};

WizardBodyTemplate.propTypes = propTypes;
WizardBodyTemplate.defaultProps = defaultProps;

export default WizardBodyTemplate;
